import { Big } from 'big.js';
import { formatUnits, parseUnits } from 'viem';
import { bigIntToBig, formatBigDisplay, formatBigEnUS, type NumberFormatOptions } from './bigUtils';

export type { NumberFormatOptions as TokenNumberFormatOptions };

export interface TokenDetail {
  decimals: number;
  display_precision: number;
  symbol?: string; // Optional: if needed for currency style formatting in the future
}

/**
 * Converts a BigInt token value (in its smallest unit, like wei) to a human-readable number.
 * @param value The BigInt value in the smallest unit.
 * @param decimals The number of decimals the token uses.
 * @returns The human-readable number string. Returns '0' if value or decimals are undefined.
 */
export function bigIntToDecimalString(value: bigint, decimals: number): string {
  if (value == null || decimals == null) {
    return '0';
  }
  return formatUnits(value, decimals);
}

/**
 * Converts values like Price and Size from api, that represent smart contract number with offset for decimals
 * @param offsetValue The numeric value without decimal part. Always positive.
 * @param decimals The number of decimals original value was offset before.
 * @returns The human-readable number.
 */
export function offsetNumberToNumber(offsetValue: number, decimals: number): number {
  if (offsetValue > Number.MAX_SAFE_INTEGER) {
    // console.log(`offsetNumberToNumber: Input (${offsetValue}) exceeds MAX_SAFE_INTEGER, precision may decline`);
  }

  return offsetValue / 10 ** decimals;
}

/**
 * Converts a human-readable decimal string (e.g. "2.3") to bigint token value (in smallest units)
 * for API/internal contract calls.
 * @param value decimal amount as string (e.g. "2.3")
 * @param decimals number of decimals (e.g. 18)
 * @returns bigint in smallest unit (e.g. 2300000000000000000n)
 */
export function decimalStringToBigInt(value: string, decimals: number): bigint {
  if (value == null || decimals == null || value.trim() === '') {
    return 0n;
  }
  return parseUnits(value, decimals);
}

/**
 * Converts a BigInt token value (smallest unit) to a locale-formatted string.
 * Uses the shared number formatting utilities.
 * @param value The BigInt value in the smallest unit.
 * @param decimals The number of decimals the token uses.
 * @param options Formatting options, including the custom displayNonZeroAsMinimal.
 * @returns A locale-formatted string representation of the number.
 */
export function bigIntToFormattedString(value: bigint, decimals: number, options?: NumberFormatOptions): string {
  return bigIntToFormattedStringWithFormatter(value, decimals, options, formatBigEnUS);
}

function bigIntToFormattedStringWithFormatter(
  value: bigint,
  decimals: number,
  options: NumberFormatOptions | undefined,
  format: (value: Big, options?: NumberFormatOptions) => string
): string {
  if (value == null || decimals == null) {
    return '';
  }
  const bigValue = bigIntToBig(value, decimals);
  const minDigits = options?.minimumFractionDigits ?? 2;
  const threshold = new Big(10).pow(-minDigits);

  if (options?.displayNonZeroAsMinimal && !bigValue.eq(0) && bigValue.abs().lt(threshold)) {
    const minimalStr = format(threshold, { ...options, minDecimals: minDigits });
    const prefix = bigValue.gt(0) ? '<' : '>';
    return `${prefix}${minimalStr}`;
  }

  return format(bigValue, options);
}

/** Browser-locale display counterpart. Do not use this for strings that feed an input or parser. */
export function bigIntToFormattedStringDisplay(value: bigint, decimals: number, options?: NumberFormatOptions): string {
  return bigIntToFormattedStringWithFormatter(value, decimals, options, formatBigDisplay);
}

/**
 * Converts a BigInt token value (smallest unit) to a locale-formatted string,
 * using token-specific details for default formatting.
 * @param value The BigInt value in the smallest unit.
 * @param token The token object with details like decimals and display_precision.
 * @param options Custom formatting options to override defaults.
 * @returns A locale-formatted string representation of the token amount.
 */
export function formatTokenAmount(value?: bigint, token?: TokenDetail, options?: Partial<NumberFormatOptions>): string {
  return formatTokenAmountWithFormatter(value, token, options, bigIntToFormattedString);
}

function formatTokenAmountWithFormatter(
  value: bigint | undefined,
  token: TokenDetail | undefined,
  options: Partial<NumberFormatOptions> | undefined,
  format: (value: bigint, decimals: number, options?: NumberFormatOptions) => string
): string {
  if (value == null || !token) {
    return '--.--';
  }

  const tokenSpecificDefaults: NumberFormatOptions = {
    minDecimals: token.display_precision,
    maxDecimals: token.display_precision,
    displayNonZeroAsMinimal: false,
    trailingZeroDisplay: 'stripIfInteger',
  };

  const finalOptions: NumberFormatOptions = {
    ...tokenSpecificDefaults,
    ...options, // override defaults
  };

  return format(value, token.decimals, finalOptions);
}

/** Browser-locale display counterpart. Inputs and values that are parsed again remain canonical. */
export function formatTokenAmountDisplay(value?: bigint, token?: TokenDetail, options?: Partial<NumberFormatOptions>): string {
  return formatTokenAmountWithFormatter(value, token, options, bigIntToFormattedStringDisplay);
}

/**
 * Generates a placeholder string for input fields based on token's display precision.
 * @param precision The token display decimals precision
 * @param fallback Optional fallback placeholder if token is not provided (default: "0.00")
 * @returns A placeholder string with zeros matching the token's display precision
 */
export function getTokenPlaceholder(precision?: number, fallback: string = '0.00'): string {
  if (precision == null) {
    return fallback;
  }
  if (precision === 0) {
    return '0';
  }
  return `0.${'0'.repeat(precision)}`;
}

export const formatFundingRatePercentage = (fundingRate: number): string => {
  if (fundingRate == null || !isFinite(fundingRate)) {
    return '--%';
  }
  return `${((fundingRate / 1_000_000) * 100).toFixed(4)}%`;
};

/**
 * Maximum absolute annualized funding rate (in percent) that will ever be DISPLAYED.
 *
 * The economic steady-state ceiling under the pricing-bot's windowed funding limiter is
 * |APR| <= max_funding_bps_per_hour * 8760h. `max_funding_bps_per_hour` is a per-environment
 * pricing-bot config value (not a fixed protocol constant, and not exposed to the FE), so we
 * cannot import it here. It is CURRENTLY 0.2 bps/h, which gives a ceiling of ≈ 17.5%.
 *
 * This 50% cap is a deliberately LOOSE display guard — about 2.85x that current ceiling — so a
 * single transient or anomalous funding event, a briefly-stale interval, or a moderate config
 * change (up to ~0.57 bps/h) can never render an absurd APR while the limiter converges, and
 * without a code change. If `max_funding_bps_per_hour` is ever raised past ~0.57 bps/h, raise
 * this cap to match. Display guard only — it touches no on-chain, economic, or stored value.
 */
export const MAX_DISPLAY_FUNDING_APR_PERCENT = 50;

/**
 * Annualize a single funding event's rate into an APR percentage string.
 *
 * @param fundingRate One funding event's rate in micros (10^-6 fraction) — i.e.
 *   `FundingEvent.rate` (`MarketData.fundingRateValue`). Signed.
 * @param fundingIntervalSec Length of the funding interval in seconds
 *   (`funding_interval_sec` = `fundingRateCountdownMinutes * 60`). Events per day is
 *   derived as `86_400 / fundingIntervalSec` rather than hardcoded to 24, so the figure
 *   stays correct as Monad's block time (and therefore the block-counted funding interval)
 *   shrinks below 1h.
 * @returns APR as a fixed(2) string with the sign preserved, or `'--'` when the interval
 *   is missing / non-positive / non-finite (never divides by it). The magnitude is clamped
 *   to {@link MAX_DISPLAY_FUNDING_APR_PERCENT}.
 *
 * APR% = (fundingRate / 1e6 * 100) * (86_400 / fundingIntervalSec) * 365
 */
export const formatAnnualizedFundingRate = (fundingRate: number, fundingIntervalSec: number): string => {
  if (fundingRate == null || !isFinite(fundingRate)) {
    return '--';
  }
  if (fundingIntervalSec == null || !isFinite(fundingIntervalSec) || fundingIntervalSec <= 0) {
    return '--';
  }
  const eventsPerDay = 86_400 / fundingIntervalSec;
  const fundingPercentage = (fundingRate / 1_000_000) * 100;
  const apr = fundingPercentage * eventsPerDay * 365;
  const cappedApr = Math.sign(apr) * Math.min(Math.abs(apr), MAX_DISPLAY_FUNDING_APR_PERCENT);
  return cappedApr.toFixed(2);
};

/**
 * Annualized-APR display string for a funding rate, or `null` when no annualized
 * figure should be surfaced at all.
 *
 * Owns the "should we show an annualized APR, and what is it" decision so both
 * funding call sites (the market-metrics panel and the markets table) stay
 * one-liners instead of repeating the finite-guard + the annualization call.
 * The maths is delegated to {@link formatAnnualizedFundingRate} unchanged — same
 * formula, same {@link MAX_DISPLAY_FUNDING_APR_PERCENT} clamp, same sign
 * handling, same interval-falsy `'--'` fallback.
 *
 * @param fundingRate One funding event's rate in micros (signed) — see
 *   {@link formatAnnualizedFundingRate}.
 * @param fundingIntervalSec Funding interval in seconds — see
 *   {@link formatAnnualizedFundingRate}.
 * @returns The clamped, sign-preserving `fixed(2)` APR string; `'--'` when the
 *   rate is finite but the interval is missing / non-positive / non-finite; or
 *   `null` when the rate itself is null / non-finite — the signal for the caller
 *   to render the plain per-interval percentage with no annualized tooltip.
 *
 * NOTE: this annualizes the LATEST single funding event — a valid instantaneous
 * reading, but noisy because the windowed funding-rate limiter makes consecutive
 * events uneven. Smoothing is a scoped follow-up: see the
 * `funding-apr-annualization` memory entry and
 * {@link FUNDING_APR_SMOOTHING_WINDOW}.
 */
export const formatFundingAprDisplay = (fundingRate: number, fundingIntervalSec: number): string | null => {
  if (fundingRate == null || !isFinite(fundingRate)) {
    return null;
  }
  return formatAnnualizedFundingRate(fundingRate, fundingIntervalSec);
};
